import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Skeleton,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from "@mui/material";
import {
  Person as PersonIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  Lightbulb as LightbulbIcon,
  Quiz as QuizIcon,
  BarChart as BarChartIcon,
  ShowChart as LineChartIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import AnnouncementsList from "../../components/AnnouncementsList";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import api from "../../api";
import { sortHierarchyItems } from "../../utils/hierarchyOrdering";

// Transform API student (StudentDetail) to dashboard format
const transformStudentFromApi = (raw) => {
  const student = raw.student || {};
  const firstName = (student.first_name || "").trim() || "Student";
  const fullName = raw.display_name
    || [student.first_name, student.last_name].filter(Boolean).join(" ").trim()
    || student.email
    || "Student";
  const assigned = raw.assigned_courses || raw.assignedCourses || [];
  const seenCourseIds = new Set();
  const courses = assigned
    .filter((ac) => {
      const cid = ac.course_id || ac.course?.id;
      if (!cid || seenCourseIds.has(cid)) return false;
      seenCourseIds.add(cid);
      return true;
    })
    .map((ac) => ({
      id: ac.course_id || ac.course?.id,
      name: ac.course?.name || "Course",
      subscriptionStatus: "Enrolled",
      latestReport: null,
    }));
  return {
    id: String(raw.child_id),
    name: firstName,
    fullName,
    avatar: firstName.charAt(0).toUpperCase(),
    targetSchoolName: raw.targetSchool?.name || raw.target_school?.name || null,
    targetSchoolId: raw.target_school_id || raw.targetSchool?.id || null,
    courses,
  };
};

// Mock: Readiness meter (overall readiness %)
const getReadinessScore = (student) => {
  if (!student) return 0;
  const scores = student.courses.map((c) => c.latestReport?.score || 0);
  return scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
};

// Four colors for up to four subjects (cycle if more)
const SUBJECT_CHART_COLORS = ["#3f51b5", "#4caf50", "#ff9800", "#e91e63"];

const PARENT_PATH_PREFIX = process.env.REACT_APP_PARENT_PREFIX || "parent";

const Dashboard = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [weeklyPerformanceBySubject, setWeeklyPerformanceBySubject] = useState([]);
  const [weakAreasFromApi, setWeakAreasFromApi] = useState([]);
  const [recentTestsFromApi, setRecentTestsFromApi] = useState([]);
  const [homeworkFromApi, setHomeworkFromApi] = useState([]);
  const [recommendationsFromApi, setRecommendationsFromApi] = useState([]);
  const [overallPerformanceFromApi, setOverallPerformanceFromApi] = useState(null);
  const [schoolReadinessFromApi, setSchoolReadinessFromApi] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [subjectChartType, setSubjectChartType] = useState("line");
  const [urgentAnnouncements, setUrgentAnnouncements] = useState([]);

  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await api.get("parent/students", {
          params: { per_page: 100, page: 1 },
        });
        if (response.data?.success && response.data?.data) {
          const paginator = response.data.data;
          const list = Array.isArray(paginator.data) ? paginator.data : paginator;
          const transformed = (Array.isArray(list) ? list : []).map(transformStudentFromApi);
          const uniqueById = transformed.filter((s, i, arr) => arr.findIndex((x) => x.id === s.id) === i);
          setStudents(uniqueById);
          if (uniqueById.length === 1) {
            setSelectedStudentId(uniqueById[0].id);
          }
        } else {
          setStudents([]);
        }
      } catch (err) {
        console.error("Error fetching parent students:", err);
        setError(err.response?.data?.message || "Failed to load students");
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, []);

  useEffect(() => {
    const fetchUrgentAnnouncements = async () => {
      try {
        const response = await api.get("parent/announcements");
        if (response.data?.success && response.data?.data) {
          const list = Array.isArray(response.data.data) ? response.data.data : [];
          const now = new Date();
          const cutoff = new Date(now.getTime() - 24 * 60 * 60 * 1000); // 24 hours ago
          const urgent = list.filter((a) => {
            if ((a.priority_level || "").toLowerCase() !== "urgent") return false;
            const created = a.created_at ? new Date(a.created_at) : null;
            return created && created >= cutoff;
          });
          setUrgentAnnouncements(urgent);
        } else {
          setUrgentAnnouncements([]);
        }
      } catch (err) {
        console.error("Error fetching announcements:", err);
        setUrgentAnnouncements([]);
      }
    };
    fetchUrgentAnnouncements();
  }, []);

  const selectedStudent = useMemo(
    () => students.find((s) => s.id === selectedStudentId) || null,
    [students, selectedStudentId]
  );

  const filteredStudents = useMemo(() => {
    if (!selectedStudentId) return students;
    return students.filter((s) => s.id === selectedStudentId);
  }, [students, selectedStudentId]);

  // Legacy: kept for compatibility (no longer used; subject performance uses weeklyPerformanceBySubject)
  const subjectPerformanceData = useMemo(() => [], []);

  useEffect(() => {
    if (!selectedStudentId) {
      setWeeklyPerformanceBySubject([]);
      setOverallPerformanceFromApi(null);
      setSchoolReadinessFromApi([]);
      const fetchAllData = async () => {
        try {
          const [weakRes, recRes] = await Promise.all([
            api.get("parent/weak-areas"),
            api.get("parent/recommendations"),
          ]);
          if (weakRes.data?.success && weakRes.data?.data?.weak_areas) {
            setWeakAreasFromApi(weakRes.data.data.weak_areas);
          } else {
            setWeakAreasFromApi([]);
          }
          if (recRes.data?.success && recRes.data?.data) {
            setRecommendationsFromApi(recRes.data.data.recommendations || []);
            setOverallPerformanceFromApi(recRes.data.data.overall_performance ?? null);
          } else {
            setRecommendationsFromApi([]);
            setOverallPerformanceFromApi(null);
          }
        } catch (err) {
          console.error("Error fetching weak areas/recommendations:", err);
          setWeakAreasFromApi([]);
          setRecommendationsFromApi([]);
          setOverallPerformanceFromApi(null);
        }
      };
      fetchAllData();
      return;
    }
    const fetchWeeklyPerformance = async () => {
      setPerformanceLoading(true);
      try {
        const response = await api.get(`parent/student/${selectedStudentId}/weekly-performance`);
        if (response.data?.success && response.data?.data) {
          const { subjects = [], current_week = 1, weak_areas = [], recommendations = [], overall_performance } = response.data.data;
          const schoolReadiness = Array.isArray(response.data.data.school_readiness) ? response.data.data.school_readiness : [];
          setWeeklyPerformanceBySubject(sortHierarchyItems(subjects));
          setWeakAreasFromApi(weak_areas);
          setRecommendationsFromApi(recommendations);
          setOverallPerformanceFromApi(overall_performance ?? null);
          setSchoolReadinessFromApi(schoolReadiness);
          setCurrentWeek(current_week);
        } else {
          setWeeklyPerformanceBySubject([]);
          setWeakAreasFromApi([]);
          setRecommendationsFromApi([]);
          setSchoolReadinessFromApi([]);
        }
      } catch (err) {
        console.error("Error fetching weekly performance:", err);
        setWeeklyPerformanceBySubject([]);
        setWeakAreasFromApi([]);
        setRecommendationsFromApi([]);
        setSchoolReadinessFromApi([]);
        setOverallPerformanceFromApi(null);
      } finally {
        setPerformanceLoading(false);
      }
    };
    fetchWeeklyPerformance();
  }, [selectedStudentId]);

  const weakAreas = useMemo(() => sortHierarchyItems(weakAreasFromApi), [weakAreasFromApi]);

  const readinessScore = useMemo(() => {
    if (overallPerformanceFromApi !== null && overallPerformanceFromApi !== undefined) {
      return Math.round(overallPerformanceFromApi);
    }
    if (selectedStudent) return getReadinessScore(selectedStudent);
    const allScores = filteredStudents.flatMap((s) =>
      s.courses.map((c) => c.latestReport?.score || 0)
    );
    return allScores.length
      ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length)
      : 0;
  }, [overallPerformanceFromApi, selectedStudent, filteredStudents]);

  useEffect(() => {
    const fetchRecentTests = async () => {
      try {
        const params = selectedStudentId ? { student_id: selectedStudentId } : {};
        const response = await api.get("parent/recent-tests", { params });
        if (response.data?.success && response.data?.data?.recent_tests) {
          setRecentTestsFromApi(response.data.data.recent_tests);
        } else {
          setRecentTestsFromApi([]);
        }
      } catch (err) {
        console.error("Error fetching recent tests:", err);
        setRecentTestsFromApi([]);
      }
    };
    fetchRecentTests();
  }, [selectedStudentId]);

  useEffect(() => {
    const fetchHomework = async () => {
      try {
        const params = selectedStudentId ? { student_id: selectedStudentId } : {};
        const response = await api.get("parent/homework", { params });
        if (response.data?.success && response.data?.data?.homework) {
          setHomeworkFromApi(response.data.data.homework);
        } else {
          setHomeworkFromApi([]);
        }
      } catch (err) {
        console.error("Error fetching homework:", err);
        setHomeworkFromApi([]);
      }
    };
    fetchHomework();
  }, [selectedStudentId]);

  const filteredRecentTests = useMemo(() => {
    const base = !selectedStudentId
      ? recentTestsFromApi
      : recentTestsFromApi.filter(
          (t) => String(t.student_id ?? "") === String(selectedStudentId)
        );
    return sortHierarchyItems(base);
  }, [recentTestsFromApi, selectedStudentId]);

  const filteredHomework = useMemo(() => {
    const base = !selectedStudentId
      ? homeworkFromApi
      : homeworkFromApi.filter(
          (h) => String(h.student_id ?? "") === String(selectedStudentId)
        );
    return sortHierarchyItems(base);
  }, [homeworkFromApi, selectedStudentId]);

  const filteredRecommendations = useMemo(() => {
    if (!selectedStudentId) return [];
    return recommendationsFromApi;
  }, [recommendationsFromApi, selectedStudentId]);

  const subjectPerformanceLineData = useMemo(() => {
    if (weeklyPerformanceBySubject.length === 0) return [];
    const maxWeek = Math.min(53, Math.max(1, currentWeek));
    const scoreBySubjectByWeek = {};
    weeklyPerformanceBySubject.forEach((subject) => {
      const rawData = (subject.weekly_performance || []).filter(
        (w) => w.week_number != null && w.week_number <= currentWeek
      );
      rawData.forEach((w) => {
        const wn = Number(w.week_number);
        if (!scoreBySubjectByWeek[wn]) scoreBySubjectByWeek[wn] = {};
        scoreBySubjectByWeek[wn][subject.name] = Number(w.score) ?? 0;
      });
    });
    return Array.from({ length: maxWeek }, (_, i) => {
      const w = i + 1;
      const row = { week_number: w, name: `Week ${w}` };
      weeklyPerformanceBySubject.forEach((s) => {
        row[s.name] = scoreBySubjectByWeek[w]?.[s.name] ?? 0;
      });
      return row;
    });
  }, [weeklyPerformanceBySubject, currentWeek]);

  return (
    <Box p={4} sx={{ bgcolor: "#f4f6f8", minHeight: "100vh" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h4" fontWeight={700}>
          Parent Dashboard
        </Typography>
        {students.length > 1 && (
          <FormControl sx={{ width: { xs: "100%", sm: 200 }, minWidth: { sm: 200 } }} disabled={loading}>
            <InputLabel id="select-student-label">Filter by Student</InputLabel>
            <Select
              labelId="select-student-label"
              value={selectedStudentId}
              label="Filter by Student"
              onChange={(e) => setSelectedStudentId(e.target.value)}
            >
              <MenuItem value="">All Students</MenuItem>
              {students.map((student) => (
                <MenuItem key={student.id} value={student.id}>
                  {student.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {urgentAnnouncements.length > 0 && (
        <Alert
          severity="error"
          icon={<WarningIcon fontSize="inherit" />}
          sx={{
            mb: 2,
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {urgentAnnouncements.map((ann) => (
              <Box
                key={ann.id}
                component={RouterLink}
                to="/parent/announcements"
                sx={{
                  py: 1,
                  px: 1.5,
                  bgcolor: "rgba(255,255,255,0.2)",
                  borderRadius: 1,
                  borderLeft: "4px solid #d32f2f",
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "pointer",
                  display: "block",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  {ann.title}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                  {ann.message?.length > 200 ? ann.message.slice(0, 200) + "…" : ann.message}
                </Typography>
              </Box>
            ))}
          </Box>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* ========== LEFT SECTION ========== */}
        <Grid item xs={12} md={3}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Child Overview Card - Always shows all students for this parent */}
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PersonIcon color="primary" /> Child Overview
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {loading ? (
                    <>
                      <Skeleton variant="rectangular" height={60} sx={{ mb: 2, borderRadius: 1 }} />
                      <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
                    </>
                  ) : students.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No students assigned. Add a student from the Students menu.
                    </Typography>
                  ) : (
                  students.map((student) => (
                    <Box key={student.id} sx={{ mb: 2.5, "&:last-child": { mb: 0 } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            bgcolor: selectedStudentId === student.id ? "primary.main" : "primary.light",
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: "1rem",
                          }}
                        >
                          {student.avatar}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {student.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {student.courses.length} course(s) enrolled
                          </Typography>
                        </Box>
                      </Box>
                      <List dense disablePadding sx={{ pl: 6 }}>
                        {student.courses.map((course) => (
                          <ListItem key={course.id} sx={{ px: 0, py: 0.25 }}>
                            <ListItemText
                              primary={course.name}
                              secondary={
                                <Chip
                                  label={course.subscriptionStatus}
                                  size="small"
                                  color={course.subscriptionStatus === "Expired" ? "error" : "success"}
                                  sx={{ mt: 0.5 }}
                                />
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  ))
                )}
                </Box>
              </CardContent>
            </Card>

            {/* Readiness Meter */}
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TrendingUpIcon color="primary" /> Readiness Meter
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Overall performance (average test results)
                </Typography>
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    {/* Track ring (translucent) */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        border: "4px solid",
                        borderColor: "rgba(156, 39, 176, 0.2)",
                        boxSizing: "border-box",
                      }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={selectedStudentId ? Math.min(100, Math.max(0, readinessScore)) : 0}
                      size={120}
                      thickness={4}
                      sx={{
                        color: selectedStudentId
                          ? readinessScore >= 80 ? "#4caf50" : readinessScore >= 50 ? "#ffc107" : "#f44336"
                          : "grey.400",
                        "& .MuiCircularProgress-circle": {
                          strokeLinecap: "round",
                        },
                      }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: "absolute",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      {selectedStudentId ? (
                        <>
                          <Typography
                            variant="h4"
                            fontWeight={800}
                            sx={{
                              lineHeight: 1.2,
                              color: "#000",
                            }}
                          >
                            {readinessScore}%
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: "bold",
                              color: "#000",
                            }}
                          >
                            {readinessScore >= 80 ? "Excellent" : readinessScore >= 50 ? "Good" : "Needs improvement"}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", textAlign: "center", px: 1 }}>
                          Select a student
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                {selectedStudentId && schoolReadinessFromApi.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, textAlign: "center" }}>
                      Readiness by Registered School
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" },
                        gap: 1.5,
                      }}
                    >
                      {schoolReadinessFromApi.map((s, idx) => {
                        const v = Math.min(100, Math.max(0, Number(s.average_score) || 0));
                        return (
                          <Box
                            key={`${s.target_school_id ?? ""}-${s.school_id ?? ""}-${s.school_name || idx}`}
                            role={selectedStudentId && s.target_school_id != null ? "button" : undefined}
                            tabIndex={selectedStudentId && s.target_school_id != null ? 0 : undefined}
                            onClick={() => {
                              if (!selectedStudentId || s.target_school_id == null) return;
                              navigate(`/${PARENT_PATH_PREFIX}/readiness/${selectedStudentId}/${s.target_school_id}`, {
                                state: {
                                  studentName: selectedStudent?.name,
                                  targetSchoolName: s.school_name,
                                },
                              });
                            }}
                            onKeyDown={(e) => {
                              if (!selectedStudentId || s.target_school_id == null) return;
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                navigate(`/${PARENT_PATH_PREFIX}/readiness/${selectedStudentId}/${s.target_school_id}`, {
                                  state: {
                                    studentName: selectedStudent?.name,
                                    targetSchoolName: s.school_name,
                                  },
                                });
                              }
                            }}
                            sx={{
                              textAlign: "center",
                              p: 1,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              ...(selectedStudentId && s.target_school_id != null
                                ? {
                                    cursor: "pointer",
                                    transition: "background-color 0.15s, box-shadow 0.15s",
                                    "&:hover": {
                                      bgcolor: "action.hover",
                                      boxShadow: 1,
                                    },
                                  }
                                : {}),
                            }}
                          >
                            <Box sx={{ position: "relative", display: "inline-flex" }}>
                              <CircularProgress
                                variant="determinate"
                                value={v}
                                size={72}
                                thickness={4}
                                sx={{
                                  color: v >= 80 ? "#4caf50" : v >= 50 ? "#ffc107" : "#f44336",
                                  "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
                                }}
                              />
                              <Box
                                sx={{
                                  top: 0,
                                  left: 0,
                                  bottom: 0,
                                  right: 0,
                                  position: "absolute",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography variant="caption" fontWeight={700}>{Math.round(v)}%</Typography>
                              </Box>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, fontWeight: 600 }}>
                              {s.school_name || "School"}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* ========== MIDDLE SECTION ========== */}
        <Grid item xs={12} md={5}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Subject Performance Charts */}
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 0.5 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SchoolIcon color="primary" /> Subject Performance
                  </Typography>
                  {selectedStudentId && !performanceLoading && weeklyPerformanceBySubject.length > 0 && (
                    <ToggleButtonGroup
                      value={subjectChartType}
                      exclusive
                      onChange={(_, val) => val != null && setSubjectChartType(val)}
                      size="small"
                      sx={{ "& .MuiToggleButton-root": { px: 1.5, py: 0.5 } }}
                    >
                      <ToggleButton value="bar" aria-label="Bar chart">
                        <BarChartIcon sx={{ mr: 0.5, fontSize: 18 }} /> Bar
                      </ToggleButton>
                      <ToggleButton value="line" aria-label="Line chart">
                        <LineChartIcon sx={{ mr: 0.5, fontSize: 18 }} /> Line
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                </Box>
                {!selectedStudentId ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                    Select a student to view subject performance
                  </Typography>
                ) : performanceLoading ? (
                  <>
                    <Skeleton variant="rectangular" height={180} sx={{ mb: 2, borderRadius: 1 }} />
                    <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 1 }} />
                  </>
                ) : weeklyPerformanceBySubject.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                    No subjects assigned or no performance data yet
                  </Typography>
                ) : subjectChartType === "line" ? (
                  <Box>
                    <ResponsiveContainer width="100%" height={Math.max(320, weeklyPerformanceBySubject.length * 70)}>
                      <LineChart
                        data={subjectPerformanceLineData}
                        margin={{ top: 5, right: 30, left: 25, bottom: 35 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="week_number"
                          type="category"
                          allowDecimals={false}
                          tick={{ fontSize: 12 }}
                          label={{ value: "Week", position: "insideBottom", offset: -10 }}
                        />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} label={{ value: "Score", angle: -90, position: "insideLeft", offset: 10 }} />
                        <Tooltip labelFormatter={(v) => `Week : ${v}`} />
                        <Legend
                          layout="horizontal"
                          align="center"
                          verticalAlign="top"
                          wrapperStyle={{ paddingBottom: 4 }}
                        />
                        {weeklyPerformanceBySubject.map((subject, index) => (
                          <Line
                            key={subject.id || index}
                            type="monotone"
                            dataKey={subject.name}
                            stroke={SUBJECT_CHART_COLORS[index % 4]}
                            strokeWidth={2}
                            dot={{ r: 4, fill: SUBJECT_CHART_COLORS[index % 4] }}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {weeklyPerformanceBySubject.map((subject, index) => {
                      const rawData = (subject.weekly_performance || []).filter(
                        (w) => w.week_number != null && w.week_number <= currentWeek
                      );
                      const maxWeek = Math.min(53, Math.max(1, currentWeek));
                      const scoreByWeek = Object.fromEntries(
                        rawData.map((w) => [Number(w.week_number), Number(w.score) ?? 0])
                      );
                      const chartData = Array.from({ length: maxWeek }, (_, i) => ({
                        week_number: i + 1,
                        score: scoreByWeek[i + 1] ?? 0,
                      }));
                      return (
                        <Box key={subject.id || index}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                            {subject.name}
                            {rawData.length === 0 && (
                              <Typography component="span" variant="caption" sx={{ ml: 1, fontWeight: 400, color: "text.secondary" }}>
                                (No data yet)
                              </Typography>
                            )}
                          </Typography>
                          <ResponsiveContainer width="100%" height={180}>
                            <BarChart
                              data={chartData}
                              margin={{ top: 20, right: 20, left: 25, bottom: 25 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="week_number"
                                type="category"
                                allowDecimals={false}
                                tick={{ fontSize: 12 }}
                                label={{ value: "Week", position: "insideBottom", offset: -10 }}
                              />
                              <YAxis
                                domain={[0, 100]}
                                tick={{ fontSize: 12 }}
                                label={{ value: "Score", angle: -90, position: "insideLeft", offset: 10 }}
                              />
                              <Tooltip labelFormatter={(v) => `Week : ${v}`} />
                              <Bar
                                dataKey="score"
                                fill={SUBJECT_CHART_COLORS[index % 4]}
                                radius={[4, 4, 0, 0]}
                                isAnimationActive={rawData.length > 0}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Weak Areas Breakdown */}
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LightbulbIcon color="primary" /> Weak Areas Breakdown
                </Typography>
                {weakAreas.length > 0 ? (
                  <List dense>
                    {weakAreas.map((area, idx) => (
                      <ListItem key={idx} divider sx={{ flexDirection: "column", alignItems: "flex-start" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", mb: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {area.subject_name || area.subject}
                            {(area.student_name || area.studentName) && !selectedStudentId && ` (${area.student_name || area.studentName})`}
                          </Typography>
                          <Chip
                            label={
                              area.score != null && area.score !== "" && !Number.isNaN(Number(area.score))
                                ? `${Math.round(Number(area.score))}%`
                                : "—"
                            }
                            size="small"
                            color="warning"
                          />
                        </Box>
                        {area.topic_name && (
                          <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ mb: 0.25 }}>
                            Topic: {area.topic_name}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          {area.suggestion}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No weak areas identified. Keep up the great work!
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* ========== RIGHT SECTION ========== */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Announcements */}
            <AnnouncementsList role="parent" />

            {/* Recent Tests */}
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <QuizIcon color="primary" /> Recent Tests
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate("/parent/test-scores")}
                    sx={{ textTransform: "none" }}
                  >
                    View All
                  </Button>
                </Box>
                <List dense>
                  {filteredRecentTests.slice(0, 8).map((test) => (
                    <ListItem
                      key={test.id || test.subject + test.date}
                      divider
                      sx={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                        cursor: "pointer",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                      onClick={() =>
                        navigate("/parent/test-scores", {
                          state: {
                            student_id: test.student_id || selectedStudentId || "",
                            subject_id: test.subject_id || "",
                            topic_id: test.topic_id || "",
                            subtopic_id: test.subtopic_id || "",
                            course_id: test.course_id || "",
                            attempt_type: "last",
                          },
                        })
                      }
                    >
                      <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "nowrap", gap: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ minWidth: 0, flex: "1 1 auto" }}>
                          {test.subject}
                          {test.topic_name && ` – ${test.topic_name}`}
                          {(test.student_name || test.studentName) && !selectedStudentId && ` (${test.student_name || test.studentName})`}
                        </Typography>
                        <Chip
                          label={
                            test.score != null && test.score !== "" && !Number.isNaN(Number(test.score))
                              ? `${Math.round(Number(test.score))}%`
                              : "—"
                          }
                          size="small"
                          color={(() => {
                            const n = Number(test.score);
                            if (!Number.isFinite(n)) return "default";
                            if (n >= 80) return "success";
                            if (n >= 60) return "warning";
                            return "error";
                          })()}
                          sx={{ flexShrink: 0, alignSelf: "flex-start" }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {test.date}
                      </Typography>
                    </ListItem>
                  ))}
                  {filteredRecentTests.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No recent tests
                    </Typography>
                  )}
                </List>
              </CardContent>
            </Card>

            {/* Homework Tracker */}
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AssignmentIcon color="primary" /> Homework Tracker
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Current week pending
                </Typography>
                <List dense>
                  {filteredHomework.slice(0, 8).map((hw, idx) => (
                    <ListItem key={hw.id || idx} divider sx={{ flexDirection: "column", alignItems: "flex-start" }}>
                      <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "nowrap", gap: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ minWidth: 0, flex: "1 1 auto" }}>
                          {hw.subject ? `${hw.subject}${hw.topic_name ? ` – ${hw.topic_name}` : ""}${hw.subtopic_name ? ` › ${hw.subtopic_name}` : ""}` : hw.title}
                          {(hw.student_name || hw.studentName) && !selectedStudentId && ` (${hw.student_name || hw.studentName})`}
                        </Typography>
                        <Chip
                          label={hw.type === "content_and_test" ? "Content & Test" : hw.type === "test_only" ? "Test only" : hw.type === "content_only" ? "Content only" : hw.status || "Pending"}
                          size="small"
                          color={hw.type === "content_and_test" ? "primary" : hw.type === "test_only" ? "secondary" : "default"}
                          sx={{ flexShrink: 0 }}
                        />
                      </Box>
                      {hw.dueDate && (
                        <Typography variant="caption" color="text.secondary">
                          Due: {hw.dueDate}
                        </Typography>
                      )}
                    </ListItem>
                  ))}
                  {filteredHomework.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No pending homework for the current week
                    </Typography>
                  )}
                </List>
              </CardContent>
            </Card>

            {/* Recommendations */}
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LightbulbIcon color="primary" /> Recommendations
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Based on current week performance
                </Typography>
                <List dense>
                  {filteredRecommendations.map((rec, idx) => (
                    <ListItem key={rec.id || idx} divider>
                      <ListItemText
                        primary={rec.text}
                        primaryTypographyProps={{ variant: "body2" }}
                        secondary={rec.student_name && !selectedStudentId ? rec.student_name : null}
                      />
                      {rec.type && (
                        <Chip
                          label={rec.type === "improvement" ? "Improve" : rec.type === "positive" ? "Excelling" : rec.type}
                          size="small"
                          color={rec.type === "positive" ? "success" : rec.type === "improvement" ? "warning" : "default"}
                          sx={{ ml: 1, flexShrink: 0 }}
                        />
                      )}
                    </ListItem>
                  ))}
                  {filteredRecommendations.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {selectedStudentId
                        ? "No recommendations yet. Complete tests to get personalized suggestions."
                        : "Select a student to view recommendations."}
                    </Typography>
                  )}
                </List>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
